import { useState, useEffect, useRef } from "react";
import { Button, Radio, Table, Popconfirm, Skeleton, message, Space } from "antd"
import { CloseOutlined, CheckOutlined, LineOutlined } from '@ant-design/icons';
import { Rnd } from "react-rnd";
import { useAtom } from "jotai";
import {
    isResultOptionsPanelOpenState,
    currentSerialState,
    loadingMapState,
    userNameState,
    userTraceDetectionPreferencesState,
    currentTraceDetectionPreferencesState,
    traceMapState,
    allScanDataState,
    mapState,
    traceOptionsReadyState
} from "../stateManagement/commonState";
import styles from './ResultOptionsPanel.module.css';
import { updateUserTraceDetectionPreferences, uploadResults } from "../utils/dbHelpers";
import { computeResults } from "../utils/results";
import { benzodiazepineLabels } from "../utils/detect";

export const calculateIncludeValue = ({ traceSubstance, currentResults, detected, userPreference }) => {
    if (currentResults) {
        // const resultEntry = currentResults.find(result => result.substance === traceSubstance);
        const traceSubstances = traceSubstance === 'Benzodiazepine' ? new Set(benzodiazepineLabels) : new Set([traceSubstance]);
        const resultEntries = currentResults.filter(result => traceSubstances.has(result.substance));
        if (resultEntries.length > 0) {
            const oneIsTraceOnly = resultEntries.some(result => result.is_trace_only);
            if (oneIsTraceOnly) {
                return true;
            } else {
                if (detected.length > resultEntries.length) {
                    return false;
                }
                return userPreference;
            }
        } else {
            if (detected.length) {
                return false;
            } else {
                return userPreference;
            }
        }
    }
}

const ResultOptionsPanel = () => {

    const [isResultOptionsPanelOpen, setIsResultOptionsPanelOpen] = useAtom(isResultOptionsPanelOpenState);
    const [modalWindowState, setModalWindowState] = useState({
        width: '375px',
        // height: '318px',
        // height: '367px',
        height: '416px',
        x: 10,
        y: 10,
    });
    const [currentSerial] = useAtom(currentSerialState);
    const [loadingMap] = useAtom(loadingMapState);
    const [username] = useAtom(userNameState);
    const [currentTraceDetectionPreferences, setCurrentTraceDetectionPreferences] = useAtom(currentTraceDetectionPreferencesState);
    const [traceOptionsDataSource, setTraceOptionsDataSource] = useState([]);
    const [userTraceDetectionPreferences, setUserTraceDetectionPreferences] = useAtom(userTraceDetectionPreferencesState);
    const [traceMap] = useAtom(traceMapState);
    const [allScanData, setAllScanData] = useAtom(allScanDataState);
    const [substancesInTraceMap, setSubstancesInTraceMap] = useState(new Set());
    const [map] = useAtom(mapState);
    const [traceOptionsReady, setTraceOptionsReady] = useAtom(traceOptionsReadyState);
    const calculatedTraceDetectionPreferencesRef = useRef(null);

    useEffect(() => {
        return () => {
            setTraceOptionsReady(false);
        }
    }, []);

    useEffect(() => {
        if (!currentSerial || !traceMap.length || calculatedTraceDetectionPreferencesRef.current) return;
        let scanIndex = allScanData.findIndex(e => e.key == currentSerial);
        const currentResults = scanIndex !== -1 ? allScanData[scanIndex].results : null;
        const substancesInTraceMap = new Set(traceMap.filter(e => e.label).map(e => e.label));
        setSubstancesInTraceMap(substancesInTraceMap);
        const calculatedTraceDetectionPreferences = {};
        Object.entries(userTraceDetectionPreferences).forEach(e => {
            // const detected = substancesInTraceMap.has(e[0]);
            // const detected = (e[0] === 'Benzodiazepine' ? benzodiazepineLabels : [e[0]]).some(substance => substancesInTraceMap.has(substance));
            const traceSubstanceLabels = e[0] === 'Benzodiazepine' ? benzodiazepineLabels : [e[0]];
            const detected = [...substancesInTraceMap].filter(substance => traceSubstanceLabels.includes(substance));
            calculatedTraceDetectionPreferences[e[0]] = calculateIncludeValue({ traceSubstance: e[0], currentResults, detected, userPreference: e[1] });
        });
        calculatedTraceDetectionPreferencesRef.current = calculatedTraceDetectionPreferences;
        setCurrentTraceDetectionPreferences({ ...calculatedTraceDetectionPreferences });
    }, [traceMap, currentSerial]);

    useEffect(() => {
        if (Object.keys(currentTraceDetectionPreferences || {}).length === 0) return;
        setTraceOptionsDataSource(Object.entries(currentTraceDetectionPreferences).map(e => ({
            key: e[0],
            traceSubstance: e[0],
            // detected: (e[0] === 'Benzodiazepine' ? benzodiazepineLabels : [e[0]]).some(substance => substancesInTraceMap.has(substance)),
            detected: [...substancesInTraceMap].filter(substance => (e[0] === 'Benzodiazepine' ? benzodiazepineLabels : [e[0]]).includes(substance)),
            include: e[1]
        })).sort((a, b) => {
            // Use localeCompare for robust alphabetical sorting of the traceSubstance field
            return a.traceSubstance.localeCompare(b.traceSubstance);
        }));
        let scanIndex = allScanData.findIndex(e => e.key == currentSerial);
        if (scanIndex !== -1) {
            const updatedScanData = [...allScanData];
            updatedScanData[scanIndex] = {
                ...updatedScanData[scanIndex],
                results: computeResults(map, traceMap, false, currentTraceDetectionPreferences)
            };
            setAllScanData(updatedScanData);
        }
    }, [currentTraceDetectionPreferences, substancesInTraceMap]);

    useEffect(() => {
        if (traceOptionsDataSource.length && calculatedTraceDetectionPreferencesRef.current) {
            setTraceOptionsReady(true);
        }
    }, [traceOptionsDataSource]);

    const traceOptionColumns = [
        {
            title: 'Trace Substance',
            dataIndex: 'traceSubstance',
            key: 'traceSubstance',
        },
        {
            title: 'Indicated',
            dataIndex: 'detected',
            key: 'detected',
            align: 'center',
            render: (detected) =>
                !traceOptionsReady ? <Skeleton.Avatar active={true} shape="square" /> :
                    traceMap.length === 0 ? <LineOutlined /> :
                        detected.length ? (
                            <CheckOutlined style={{ color: 'green', fontSize: 16 }} />
                        ) : (
                            <CloseOutlined style={{ color: 'red', fontSize: 16 }} />
                        ),
        },
        {
            title: 'Include',
            dataIndex: 'include',
            key: 'include',
            render: (value, traceOption) =>
                !traceOptionsReady ? <div style={{ width: '104.26px' }}><Skeleton.Button active={true} block={true} /></div> :
                    (
                        <Radio.Group
                            value={value}
                            style={{ userSelect: 'none' }}
                            onChange={(e) => {
                                const checked = e.target.value;
                                setCurrentTraceDetectionPreferences((prev) => ({
                                    ...prev,
                                    [traceOption.traceSubstance]: checked
                                }));
                            }}
                        >
                            <Radio.Button value={false}>No</Radio.Button>
                            <Radio.Button value={true}>Yes</Radio.Button>
                        </Radio.Group>
                    )
        }
    ];

    const handleTraceDetectionPreferenceChange = async () => {
        const scan = allScanData.find(e => e.key == currentSerial);
        if (scan) {
            try {
                await uploadResults({ mapID: scan.map_id, results: scan.results });
                message.success('Results updated successfully.');
            } catch (error) {
                console.error('Error uploading results:', error);
                message.error('Failed to update results. Please try again.');
            }
        }
    }

    const handleSetAsDefault = async () => {
        const updatedTraceDetectionPreferences = {};
        traceOptionsDataSource.forEach(option => {
            updatedTraceDetectionPreferences[option.traceSubstance] = option.include;
        });
        setUserTraceDetectionPreferences(updatedTraceDetectionPreferences);
        try {
            const { success } = await updateUserTraceDetectionPreferences(username, updatedTraceDetectionPreferences);
            if (!success) { throw new Error('Failed to update default user trace detection preferences.'); }
            message.success('Trace detection preferences updated successfully.');
        } catch (error) {
            console.error('Error updating user trace detection preferences:', error);
            message.error('Failed to update trace detection preferences. Please try again.');
        }
    };

    return (
        <>
            {isResultOptionsPanelOpen && (
                <div style={{
                    position: 'fixed',
                    top: '0',
                    left: '0',
                    width: '100%',
                    height: '100%',
                    zIndex: 100,
                    pointerEvents: 'none'
                }}
                >
                    <Rnd
                        dragHandleClassName="drag-handle"
                        style={{
                            pointerEvents: 'auto',
                            borderRadius: '16px',
                            position: 'relative',
                            boxShadow: '3px 3px 10px 3px #dddddd',
                        }}
                        size={{ width: modalWindowState.width, height: modalWindowState.height }}
                        position={{ x: modalWindowState.x, y: modalWindowState.y }}
                        onDragStop={(e, d) => {
                            setModalWindowState(prev => ({ ...prev, x: d.x, y: d.y }))
                        }}
                        enableResizing={false}
                        onResize={(e, direction, ref, delta, position) => {
                            setModalWindowState(prev => ({
                                ...prev,
                                width: ref.style.width,
                                height: ref.style.height,
                                ...position
                            }));
                        }}
                        minWidth={150}
                        minHeight={150}
                        maxWidth={900}
                        maxHeight={900}
                    >
                        <div className="drag-handle" style={{
                            zIndex: '102',
                            position: 'absolute',
                            width: '100%',
                            height: '32px',
                            backgroundColor: 'rgba(0, 0, 0, 0.1)',
                            borderRadius: '16px 16px 0 0',
                        }}>
                            <p style={{ textAlign: 'center', lineHeight: '32px', margin: '0', cursor: 'default' }}>Result Options{currentSerial ? ` | Serial ${currentSerial}` : null}</p>
                        </div>
                        <div
                            style={{
                                backgroundColor: '#f5f5f5',
                                position: 'relative',
                                top: '0px',
                                left: '0px',
                                width: `calc(${modalWindowState.width})`,
                                height: `calc(${modalWindowState.height})`,
                                zIndex: 101,
                                borderRadius: '16px',
                                padding: '16px'
                            }}>
                            <div
                                style={{
                                    width: '100%',
                                    height: `calc(${modalWindowState.height} - 40px)`,
                                    // marginTop: 'calc(24px + 32px)',
                                    marginTop: 'calc(24px)',
                                    overflow: 'hidden',
                                }}
                                className={styles.popup}
                                align="center"
                            >
                                <Table size="small" columns={traceOptionColumns} dataSource={traceOptionsDataSource} pagination={false} />
                                <Space>
                                    <Popconfirm
                                        title="This will update the results for this scan."
                                        description="Are you sure you want to continue?"
                                        okText="Yes"
                                        cancelText="No"
                                        onConfirm={handleTraceDetectionPreferenceChange}
                                    >
                                        <Button type="primary" ghost style={{ marginTop: '8px' }} disabled={loadingMap || traceMap.length === 0}>Save</Button>
                                    </Popconfirm>
                                    <Popconfirm
                                        title="This will update trace detection settings for all new scans."
                                        description="Are you sure you want to continue?"
                                        okText="Yes"
                                        cancelText="No"
                                        onConfirm={handleSetAsDefault}
                                    >
                                        <Button type="primary" ghost style={{ marginTop: '8px' }} disabled={loadingMap}>Set as new default</Button>
                                    </Popconfirm>
                                </Space>
                            </div>
                        </div>
                        <Button
                            type="primary"
                            danger
                            shape="circle"
                            size="small"
                            icon={<CloseOutlined style={{ fontSize: '9px' }} />}
                            onClick={() => {
                                setIsResultOptionsPanelOpen(false);
                            }}
                            style={{
                                position: 'fixed',
                                top: '8px',
                                left: '16px',
                                minWidth: '16px',
                                width: '16px',
                                height: '16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                zIndex: 103,
                            }}
                        />
                    </Rnd >
                </div >
            )}
        </>
    )
}

export default ResultOptionsPanel;
